import React, { useState, useRef, useEffect } from "react";
import { LogIn, LogOut,  ChevronDown, User as UserIcon, Snail } from "lucide-react";

import { useAuthStore } from "../authStore";
import { useConfigStore } from "@/useConfigStore";

interface AccountProps {
  className?: string;
}

const Account: React.FC<AccountProps> = ({ className = "" }) => {
  const { config } = useConfigStore()
 

  const { user, isLoading, setLoading,  signOut } = useAuthStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);



  // Initialize auth on moun

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const idToken = params.get("idToken");
      const state = params.get("state");
  
      if (!(code || idToken)) return;     // nothing to do
      if (!config) return;                 // wait until config is loaded, then rerun
  
      const run = async () => {
        try {
          setLoading(true);
          const requestBody: { code?: string; idToken?: string; state?: string } = {};
          if (code) requestBody.code = code;
          if (idToken) requestBody.idToken = idToken;
          if (state) requestBody.state = state;
  
          const res = await fetch(config.AUTH_CALLBACK_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          });
          const data = await res.json();
  
          if (data.accessToken && data.user) {
            const userData = {
              id: data.user.uid,
              name: data.user.displayName || data.user.email?.split('@')[0],
              email: data.user.email,
              picture: data.user.photoURL || '',
              user_plan: data.user.user_plan || "free",
            };
            localStorage.setItem('pomozid_user', JSON.stringify(userData));
            localStorage.setItem('pomozid_token', data.accessToken);
            useAuthStore.setState({ user: userData, isLoading: false });
            useAuthStore.getState().loadUserFromStorage();
            window.history.replaceState({}, document.title, "/");
          } else {
            alert(data.error || "Google login failed.");
          }
        } catch (err) {
          console.error("Google login error:", err);
          alert("Google login failed. Please try again.");
        } finally {
          setLoading(false);
        }
      };
      run();
    }, [config, setLoading]);

  const handleSignInClick = async () => {
    try {
      // Determine the redirect URI based on current origin
      const redirectUri = `${window.location.origin}`;

         if (!config) {
        console.error("Configuration not loaded");
        setLoading(false);
        return;
      }
      
      // Send redirect_uri in POST body instead of query string
      const res = await fetch(`${config.AUTH_URL_BASE}/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ redirectUri }),
      });
      
      const data = await res.json();

      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        alert("Unable to start Google login flow.");
      }
    } catch (err) {
      console.error("Error starting Google OAuth flow:", err);
      alert("Google Sign-In failed to start.");
    }
  };

  const handleSignOutClick = () => {
    signOut();
    setIsDropdownOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        className="flex items-center gap-2 p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span className="sr-only">Loading user</span>
          </>
        ) : user  ? (
        user.picture?  
          <>
            <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
            <ChevronDown size={16} className={`transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
          </> :
     <>
    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-500  text-xs font-medium">
      {user.name?.[0]?.toUpperCase() || "?"}
    </div>
    <ChevronDown
      size={16}
      className={`transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
    />
  </>

        ) : (
          <>
            <UserIcon size={24} />
            <ChevronDown size={16} className={`transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
          </>
        )}
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
          {user ? (
            <>
              {/* User Info */}
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
                {user.picture ?  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" /> :    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-500  text-xs text-white font-medium">
      {user.name?.[0]?.toUpperCase() || "?"}
    </div>}
               
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                </div>
              </div>

              {/* Menu */}
              <div className="py-1">
                <button
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  onClick={() => {
                    setIsDropdownOpen(false);
                    // Implement account settings
                  }}
                >
                  <Snail size={16} />
                  {`${user.user_plan == 'free' ? "upgrade to PRO " : `PRO member` }`}
                </button>
                <button
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  onClick={handleSignOutClick}
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            </>
          ) : (
            <div className="py-1">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm text-gray-600">Sign in to sync your data across devices</p>
              </div>
              <button
                className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                onClick={handleSignInClick}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn size={16} />
                    Sign in with
                    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 48 48">
                      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
                      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
                      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
                      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
                    </svg>
                    oogle
                  </>
                )}
              </button>
              <div className="px-4 py-2 text-xs text-gray-500">
                By signing in, you agree to our Terms of Service and Privacy Policy
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Account;